const SWIPER_MAIN_OPTS = {
  wrapperClass: 'ca-pdp-gallery__main-track',
  slideClass: 'ca-pdp-gallery__slide',
  slideActiveClass: 'ca-pdp-gallery__slide--active',
  slideNextClass: 'ca-pdp-gallery__slide--next',
  slidePrevClass: 'ca-pdp-gallery__slide--prev',
};

const SWIPER_THUMBS_OPTS = {
  wrapperClass: 'ca-pdp-gallery__thumbs-track',
  slideClass: 'ca-pdp-gallery__thumb',
};

class CaPdpGallery extends HTMLElement {
  constructor() {
    super();
    this.mainSwiper = null;
    this.thumbsSwiper = null;
    this.mobileBreakpoint = 768;
    this.onVariantChange = null;
  }

  connectedCallback() {
    if (typeof Swiper === 'undefined') {
      window.addEventListener('load', () => this.init(), { once: true });
      return;
    }

    this.init();
  }

  disconnectedCallback() {
    this.destroy();
    if (window.jQuery && this.onVariantChange) {
      window.jQuery(document).off('variant:change.caPdpGallery', this.onVariantChange);
    }
  }

  init() {
    if (typeof Swiper === 'undefined' || this.mainSwiper) return;

    const mainEl = this.querySelector('.ca-pdp-gallery__main');
    const thumbsEl = this.querySelector('.ca-pdp-gallery__thumbs');
    if (!mainEl) return;

    const initialSlide = parseInt(this.dataset.initialSlide || '0', 10);
    const isDesktop = window.innerWidth >= this.mobileBreakpoint;
    const slideCount = mainEl.querySelectorAll('.ca-pdp-gallery__slide').length;
    const enableLoop = slideCount > 1;
    const mobileSlidesPerView = slideCount > 1 ? 1.15 : 1;

    if (isDesktop && thumbsEl) {
      this.thumbsSwiper = new Swiper(thumbsEl, {
        ...SWIPER_THUMBS_OPTS,
        slidesPerView: 4,
        spaceBetween: 8,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        freeMode: false,
        breakpoints: {
          992: {
            slidesPerView: 5,
            spaceBetween: 8,
          },
          1200: {
            slidesPerView: 6,
            spaceBetween: 8,
          },
        },
      });
    }

    this.mainSwiper = new Swiper(mainEl, {
      ...SWIPER_MAIN_OPTS,
      initialSlide,
      slidesPerView: mobileSlidesPerView,
      spaceBetween: 12,
      centeredSlides: false,
      autoHeight: !isDesktop,
      loop: isDesktop && enableLoop,
      watchOverflow: true,
      breakpoints: {
        768: {
          slidesPerView: 1,
          spaceBetween: 0,
          autoHeight: false,
        },
      },
      navigation: {
        nextEl: this.querySelector('.ca-pdp-gallery__nav--next'),
        prevEl: this.querySelector('.ca-pdp-gallery__nav--prev'),
        disabledClass: 'ca-pdp-gallery__nav--disabled',
      },
      pagination: {
        el: this.querySelector('.ca-pdp-gallery__pagination'),
        clickable: true,
        bulletClass: 'ca-pdp-gallery__pagination-bullet',
        bulletActiveClass: 'ca-pdp-gallery__pagination-bullet--active',
      },
      thumbs: this.thumbsSwiper
        ? {
            swiper: this.thumbsSwiper,
            slideThumbActiveClass: 'ca-pdp-gallery__thumb--active',
          }
        : undefined,
      on: {
        init: () => this.syncThumbs(),
        slideChange: () => this.syncThumbs(),
      },
    });

    if (this.thumbsSwiper) {
      this.thumbsSwiper.on('click', () => {
        const clickedIndex = this.thumbsSwiper.clickedIndex;
        if (clickedIndex === undefined || clickedIndex < 0) return;

        const slide = this.thumbsSwiper.slides[clickedIndex];
        if (slide?.classList.contains('is_varhide')) return;

        if (enableLoop) {
          this.mainSwiper.slideToLoop(clickedIndex);
        } else {
          this.mainSwiper.slideTo(clickedIndex);
        }
      });
    }

    if (window.jQuery) {
      this.onVariantChange = (event, variant) => {
        if (variant?.featured_media?.id) {
          this.slideToMediaId(variant.featured_media.id);
        }
      };
      window.jQuery(document).on('variant:change.caPdpGallery', this.onVariantChange);
    }

    this.syncThumbs();
  }

  getVisibleMainSlides() {
    return Array.from(this.querySelectorAll('.ca-pdp-gallery__main .ca-pdp-gallery__slide')).filter(
      (slide) => !slide.classList.contains('is_varhide')
    );
  }

  getSlideIndexByMediaId(mediaId) {
    const slides = this.querySelectorAll('.ca-pdp-gallery__main .ca-pdp-gallery__slide');
    for (let i = 0; i < slides.length; i += 1) {
      if (slides[i].dataset.mdid === String(mediaId) && !slides[i].classList.contains('is_varhide')) {
        return i;
      }
    }
    return -1;
  }

  slideToMediaId(mediaId) {
    const index = this.getSlideIndexByMediaId(mediaId);
    if (index < 0 || !this.mainSwiper) return;

    if (this.mainSwiper.params.loop) {
      this.mainSwiper.slideToLoop(index);
    } else {
      this.mainSwiper.slideTo(index);
    }

    this.syncThumbs();
  }

  refreshVariantSlides() {
    const visibleSlides = this.getVisibleMainSlides();
    if (!visibleSlides.length || !this.mainSwiper) return;

    const activeSlide = this.mainSwiper.slides[this.mainSwiper.activeIndex];
    if (activeSlide?.classList.contains('is_varhide')) {
      const firstVisibleIndex = Array.from(
        this.querySelectorAll('.ca-pdp-gallery__main .ca-pdp-gallery__slide')
      ).findIndex((slide) => !slide.classList.contains('is_varhide'));

      if (firstVisibleIndex >= 0) {
        if (this.mainSwiper.params.loop) {
          this.mainSwiper.slideToLoop(firstVisibleIndex);
        } else {
          this.mainSwiper.slideTo(firstVisibleIndex);
        }
      }
    }

    this.syncThumbs();
  }

  syncThumbs() {
    if (!this.mainSwiper) return;

    const realIndex = this.mainSwiper.realIndex;
    const thumbs = this.querySelectorAll('.ca-pdp-gallery__thumbs .ca-pdp-gallery__thumb');

    thumbs.forEach((thumb, index) => {
      thumb.classList.toggle('ca-pdp-gallery__thumb--active', index === realIndex);
    });

    if (this.thumbsSwiper && realIndex >= 0) {
      this.thumbsSwiper.slideTo(realIndex);
    }
  }

  destroy() {
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }
  }
}

customElements.define('ca-pdp-gallery', CaPdpGallery);
